import { computed } from 'vue'
import { cloneDeep, isNil } from 'lodash'
import config from '@/config'
import { DATE_FORMAT_ISO } from '@/config/constants'
import { DateTimeHelper } from '@/helpers/DateTime'
import { SortUtil } from '@/utils/Sorting'
import { resolve } from '@/modules/PartnerWidget/helpers/strings'
import { isRequired, NotFoundError } from '@/utils/CustomErrors'

import useBackend from '@/hooks/useBackend'
import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

const { buildURL, getBaseUrl, setup } = useBackend()
const { bundleConfig, displayMode, theme } = usePartnerWidgetBundleLoader()

setup('/partnerwidget', config.microserviceBaseUrl)

// TODO: Already defined in usePersonSpecificProduct
function getProduct(person, productId) {
  return person?.products?.products[productId]
}

// TODO: Already defined in usePersonSpecificProduct
function getProductPrice(person, productId) {
  return getProduct(person, productId).prices.find(p => !!p.selected)
}

/**
 * The backend used id whereas the frontend works with productId everywhere. This function provides the mapping logic.
 * @param __products Set of products
 * @param __productId Id to identify the product
 */
function mapIdToProductId(__products, __productId) {
  __products[__productId].productId = __productId
  delete __products[__productId].id
}

/**
 * Returns all existing products of a group sorted by the order field
 * @param __groups
 * @param __products
 * @param groupId
 * @return []
 */
function _getSortedProductsFromGroup(
  __groups = isRequired('groups'),
  __products = isRequired('products'),
  groupId = isRequired('groupId')
) {
  let groupsObject = { ...__groups }
  if (!groupsObject[groupId]) throw new NotFoundError('group with groupId', groupId)
  const productsCopy = cloneDeep(__products)
  return groupsObject[groupId].products
    .map(productId => {
      mapIdToProductId(productsCopy, productId)
      return productsCopy[productId]
    })
    .filter(product => product)
    .sort(SortUtil.sortByOrder)
}

/**
 * Returns all existing products of a category sorted by the order field
 * @param categoryId
 * @return []
 */
function getSortedProductsFromCategory(productsStructure, categoryId = isRequired('categoryId')) {
  const { categories, groups, products } = productsStructure

  return categories[categoryId].groups
    .reduce((acc, groupId) => acc.concat(_getSortedProductsFromGroup(groups, products, groupId)), [])
    .sort(SortUtil.sortByOrder)
}

function getReferrerParams() {
  if (window.location && window.location.search) {
    const { search } = window.location
    const urlSearchParams = new URLSearchParams(search.toLowerCase())
    const params = {}
    urlSearchParams.forEach((value, key) => (params[key] = value))
    return params
  }
  return {}
}

function hasLabel(key) {
  return bundleConfig?.labels ? !!bundleConfig.labels[key] : false
}

function getLabel(key, params = {}) {
  if (!hasLabel(key)) {
    return key
  }
  return resolve(bundleConfig.labels[key], params)
}

function getOfferText(persons, showExampleOfferText = true) {
  if (!persons || persons.length < 1) return ''
  const person = persons[0]
  const age = person?.age || DateTimeHelper.getAge(person?.personData.dateOfBirth)
  const gender = person.personData.gender.toLowerCase()

  // currently no labels exist for unborn child
  const isBornChild = age > 0 && age < 16
  const textVariant = isBornChild ? 'child' : gender

  if (showExampleOfferText) {
    return getLabel('preview_example_' + textVariant, { age })
  }

  return getLabel('personal_offer_text_' + textVariant, { age })
}

export default function usePartnerWidget() {
  const partnerWidgetTheme = computed(() => {
    if (Object.keys(bundleConfig).length === 0) {
      return theme.value
    }
    return bundleConfig.theme
  })

  const partnerWidgetDisplayMode = computed(() => {
    if (Object.keys(bundleConfig).length === 0) {
      return displayMode.value
    }
    return bundleConfig.displayMode
  })

  function getCustomThemeCssUrl(code, lastModifiedDate) {
    return Promise.resolve(buildURL(`${getBaseUrl}/public/bundles/${code}/css?v=${lastModifiedDate}`))
  }

  function getPackageTotalPrice(packageProducts, person) {
    if (isNil(packageProducts)) {
      throw new Error('getPackageTotalPrice: Invalid parameter packageProducts', packageProducts)
    }
    if (isNil(person)) {
      throw new Error(`getPackageTotalPrice: Invalid parameter personId, ${person}`)
    }

    return packageProducts.reduce((acc, product) => {
      const value = !isNil(product.value) ? parseInt(product.value) : null
      if (!isNil(value)) {
        acc += getProductPriceOptionForFranchise(person, product.id, value).price
      } else {
        acc += getProductPrice(person, product.id).price
      }
      return acc
    }, 0)
  }

  function getProductPriceOptionForFranchise(person, productId, value) {
    if (isNil(person) || isNil(productId) || isNil(value)) {
      throw new Error(
        `getProductPriceForFranchise missing required parameter(s) person: ${person} productId: ${productId} value: ${value}`
      )
    }

    const storeProduct = getProduct(person, productId)
    const price = storeProduct.prices.find(p => p.value === value)
    if (isNil(price)) {
      throw new Error(`getProductPriceForFranchise could not find price for product ${productId} franchise: ${value}`)
    }

    return price
  }

  /**
   * Select all customize parameters form the URL
   *
   * Available parameters:
   *  - birthDate      ==> Set the date of birth from the person, alias birthdate e.g. 2001-09-09
   *  - firstName      ==> Set the first name from the person, alias firstName
   *  - gender         ==> Set the gender from the person (MALE, FEMALE, UNBORN)
   *  - zipcode        ==> Set the zipCode for the location objecct
   *  - zipcodeaddon   ==> Set the zipCodeAddon for the location objecct
   *  - locationnumber ==> Set the locationNumber for the location objecct
   *
   * @urlParams Object Get proper url parameters for setting a new person
   */
  function getQueryParams() {
    const params = getReferrerParams()
    const { locationnumber, zipcode, zipcodeaddon, birthdate, firstname } = params

    let gender
    if (params.gender) {
      gender = params.gender.toUpperCase()
    }

    let dateOfBirth
    if (DateTimeHelper.isValidBirthDate(birthdate)) {
      dateOfBirth = DateTimeHelper.formatDate(birthdate, DATE_FORMAT_ISO)
    }

    let location
    if (zipcode) {
      location = {
        locationNumber: locationnumber,
        zipCode: zipcode,
        zipCodeAddon: zipcodeaddon,
      }
    }

    return {
      gender,
      dateOfBirth,
      location,
      firstName: firstname,
    }
  }

  function isPackageValid(products) {
    const anyProductInvalid = products.some(product => {
      // product not found in basket => not valid (e.g. Kinderwunsch product not in basket, if person is male)
      if (!product) {
        return true
      }
      // special case medical private: ignore MEDICAL_PRIVATE_CONDITIONS_UNFULFILLED error, as products in basket
      // are not selected and valid will always be false
      if (
        product.id === 'PE04Produkt' &&
        product.messages.length === 1 &&
        product.messages[0].key === 'MEDICAL_PRIVATE_CONDITIONS_UNFULFILLED'
      ) {
        return false
      }
      return product.valid === false
    })
    return !anyProductInvalid
  }

  function isPackageAvailable(pack) {
    let isInvalid = false

    const firstPerson = pack.basket?.persons[0]

    // @TODO check for better way to handle products with franchises not matching for a person age
    try {
      getPackageTotalPrice(pack.products, firstPerson)
    } catch {
      return false
    }

    const products = pack.products.map(p => {
      const storeProduct = getProduct(firstPerson, p.id)

      if (!storeProduct) {
        isInvalid = true
      }

      return {
        id: p.id,
        ...storeProduct,
      }
    })

    if (isInvalid) {
      return false
    }

    return isPackageValid(products)
  }

  return {
    // Data
    partnerWidgetDisplayMode,
    partnerWidgetTheme,

    // Methods
    getCustomThemeCssUrl,
    getLabel,
    getOfferText,
    getPackageTotalPrice,
    getProductPrice,
    getProductPriceOptionForFranchise,
    getSortedProductsFromCategory,
    getQueryParams,
    hasLabel,
    isPackageAvailable,
    isPackageValid,
  }
}
